import { GenericAbortSignal } from 'axios'
import { API_REPORT_URL } from '../constants/base-urls'
import {
  ICandidateReport,
  IDataTableCandidateApplicant,
  IJobPostingReport,
  IProcessAnalisis,
  IRecruitmentReport,
  IVacancyReport,
  type INumberOfHired,
  type INumberOfHiredDataTable,
  type IPaginationParam,
  type IPaginationResponse,
  type IRecruitmentFunnel,
  type IRecruitmentFunnelCard,
} from '../types'
import { createAxiosInstance } from '../utils'

const axios = createAxiosInstance({
  baseURL: API_REPORT_URL,
  withAuth: true,
})

type FetchReportParams = {
  department?: any
  organization?: any
  education?: any
  endDate?: string
  gender?: any
  province?: any
  range?: any
  startDate?: string
  type?: any
  year?: any
  branch?: string
  jobLevel?: string
  jobType?: string
  applyLevel?: string
  paginationToken?: string
  isPaginationToken?: string
  isBack?: string
  startAt?: string
  endAt?: string
  beforePage?: number
  afterPage?: number
} & IPaginationParam

export const fetchCandidateRecruitmentFunnel = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IRecruitmentFunnel }>(`/summary/recruitment-funnel`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateNumberHired = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<INumberOfHiredDataTable> }>(`/summary/number-of-hired/datatable`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchCandidateNumberHiredChart = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: INumberOfHired }>(`/summary/number-of-hired/chart`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateAge = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/age`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateProvince = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/province`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateEducation = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/education`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateExperience = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/experience`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateGender = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/gender`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateDepartment = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/department`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateOrganization = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/candidate/organization`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeePosition = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/position`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeAge = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/age`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeBranch = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/branch`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeDepartment = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/department`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeJobLevel = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/job-level`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeGender = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/gender`, { params, signal }).then((response) => response.data.data)
}

export const fetchEmployeeJobType = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get(`/demography/employee/job-type`, { params, signal }).then((response) => response.data.data)
}

export const fetchCandidateRecruitmentFunnelCard = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IRecruitmentFunnelCard }>(`/summary/card`, { params, signal }).then((response) => response.data.data)
}

export interface ProcessReportParams extends IPaginationParam {
  departmentId?: string
}

export interface ProcesDetailReportParams extends IPaginationParam {
  vacancyId: string
}

export const fetchProcessAnalisis = (params: ProcessReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{
      data: IPaginationResponse<IProcessAnalisis>
    }>('/report/process-analysis', {
      params,
      signal,
    })
    .then((response) => response.data.data)
}
export const recruitmentReport = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IRecruitmentReport> }>(`/report/recruitment`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchProcessAnalisisDetail = (params: ProcesDetailReportParams, signal?: GenericAbortSignal) => {
  const { vacancyId, ...restParams } = params
  return axios
    .get<{
      data: IPaginationResponse<IDataTableCandidateApplicant>
    }>(`/report/process-analysis/detail/${vacancyId}`, {
      params: restParams, // pakai object tanpa vacancyId
      signal,
    })
    .then((response) => response.data.data)
}
export const candidateReport = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<ICandidateReport> }>(`/report/candidate`, { params, signal })
    .then((response) => response.data.data)
}
export const vacancyReport = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IVacancyReport> }>(`/report/vacancy`, { params, signal })
    .then((response) => response.data.data)
}
export const jobPostingReport = (params?: FetchReportParams, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IJobPostingReport> }>(`/report/job-posting`, { params, signal })
    .then((response) => response.data.data)
}
export const applicationTrendsReport = ({ year, type, month }: { year?: string; type?: 'YEAR' | 'MONTH' | 'QUARTER'; month?: string }) => {
  return axios
    .get(`/report/application-trends`, {
      params: { year, type, month },
    })
    .then((response) => response.data.data)
}
